/**
 * Node Type Registry
 *
 * Central registry for all survey question types.
 * Inspired by n8n.io's pluggable node architecture.
 *
 * Adding a new question type:
 * 1. Create a folder: nodes/types/YourType/
 * 2. Create: YourTypeNode.jsx (canvas renderer)
 * 3. Create: YourTypeConfig.jsx (property panel form)
 * 4. Create: index.js (call registerNodeType with config)
 * 5. Add import to nodes/types/index.js
 */

import React from 'react'

const nodeTypeRegistry = new Map()

export function registerNodeType(config) {
  if (!config || !config.type) {
    console.error('[NodeRegistry] Cannot register type without a type key:', config)
    return
  }
  if (nodeTypeRegistry.has(config.type)) {
    console.warn(`[NodeRegistry] Type "${config.type}" is already registered. Overwriting.`)
  }
  const commonDefaults = {
    id: null, sectionId: null,
    question: { EN: '', ZH_CN: '', ZH_HK: '' },
    answerLabel: { EN: '', ZH_CN: '', ZH_HK: '' },
    type: config.type, inputMode: null, inputRegex: null,
    minSize: 0, maxSize: 0, mandatory: false,
    answerOptionList: null, sequence: 10, status: 'A',
    parentQuestionId: null, nextQuestionMap: null, filterKey: null,
  }
  nodeTypeRegistry.set(config.type, {
    ...config,
    defaults: { ...commonDefaults, ...config.defaults },
  })
}

export function getNodeType(type) { return nodeTypeRegistry.get(type) }
export function getAllNodeTypes() { return Array.from(nodeTypeRegistry.values()) }

export function getNodeTypesForReactFlow() {
  const map = {}
  for (const [type, config] of nodeTypeRegistry) { map[type] = config.nodeComponent }
  return map
}

export function getToolbarItems() {
  return getAllNodeTypes().map(({ type, label, icon }) => ({ type, label, icon }))
}

export function renderConfigComponent(type, props) {
  const config = getNodeType(type)
  if (!config || !config.configComponent) return null
  const ConfigComponent = config.configComponent
  return ConfigComponent ? React.createElement(ConfigComponent, props) : null
}

export default nodeTypeRegistry
